/**
 * VaultContext
 * Manages global vault state and operations
 *
 * This context eliminates prop drilling for vaultedItems and vault operations
 * which are used by: App, BlackHole, TabNavigation, VaultTab, UploadTab
 */

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  ReactNode
} from 'react';
import { storageService, type VaultItem } from '../services/storage.service';

// ============================================================================
// Context Types
// ============================================================================

export interface VaultContextValue {
  /** List of all vault items */
  vaultedItems: VaultItem[];
  /** Whether vaults are currently being loaded */
  isLoadingVaults: boolean;
  /** Error that occurred during vault operations */
  vaultError: Error | null;
  /** Number of vaults (convenience property) */
  vaultCount: number;
  /** Add a new vault item */
  addVault: (vault: VaultItem) => void;
  /** Remove a vault by ID */
  removeVault: (id: string) => Promise<boolean>;
  /** Refresh vaults from storage */
  refreshVaults: () => Promise<void>;
}

// ============================================================================
// Context Creation
// ============================================================================

const VaultContext = createContext<VaultContextValue | null>(null);

VaultContext.displayName = 'VaultContext';

// ============================================================================
// Provider Component
// ============================================================================

export interface VaultProviderProps {
  children: ReactNode;
  /** Optional initial vaults for testing */
  initialVaults?: VaultItem[];
}

/**
 * VaultProvider
 * Wraps the application to provide vault state to all components
 */
export function VaultProvider({
  children,
  initialVaults
}: VaultProviderProps): JSX.Element {
  const [vaultedItems, setVaultedItems] = useState<VaultItem[]>(initialVaults ?? []);
  const [isLoadingVaults, setIsLoadingVaults] = useState<boolean>(!initialVaults);
  const [vaultError, setVaultError] = useState<Error | null>(null);

  // Load vaults from storage on mount
  useEffect(() => {
    // Skip if initial vaults were provided (testing)
    if (initialVaults) {
      return;
    }

    const loadVaults = async (): Promise<void> => {
      try {
        setIsLoadingVaults(true);
        setVaultError(null);

        // Clean up expired vaults first
        await storageService.deleteExpiredVaults();

        // Load vaults sorted by creation date (newest first)
        const vaults = await storageService.getVaultsSorted();
        setVaultedItems(vaults);
      } catch (error) {
        const vaultErr = error instanceof Error ? error : new Error('Failed to load vaults');
        setVaultError(vaultErr);
        console.error('Failed to load vaults:', error);
      } finally {
        setIsLoadingVaults(false);
      }
    };

    loadVaults();
  }, [initialVaults]);

  // Add a vault (saves to storage and updates state)
  const addVault = useCallback((vault: VaultItem): void => {
    // Optimistically add to state (newest first)
    setVaultedItems((prev) => [vault, ...prev]);

    // Persist to storage (fire and forget, but log errors)
    storageService.saveVault(vault).catch((error) => {
      console.error('Failed to save vault:', error);
      // Could optionally revert the optimistic update here
    });
  }, []);

  // Remove a vault by ID
  const removeVault = useCallback(async (id: string): Promise<boolean> => {
    try {
      const success = await storageService.deleteVault(id);

      if (success) {
        setVaultedItems((prev) => prev.filter((item) => item.id !== id));
      }

      return success;
    } catch (error) {
      console.error('Failed to remove vault:', error);
      return false;
    }
  }, []);

  // Refresh vaults from storage
  const refreshVaults = useCallback(async (): Promise<void> => {
    try {
      setIsLoadingVaults(true);
      setVaultError(null);

      // Clean up expired vaults
      await storageService.deleteExpiredVaults();

      // Reload from storage
      const vaults = await storageService.getVaultsSorted();
      setVaultedItems(vaults);
    } catch (error) {
      const vaultErr = error instanceof Error ? error : new Error('Failed to refresh vaults');
      setVaultError(vaultErr);
      console.error('Failed to refresh vaults:', error);
    } finally {
      setIsLoadingVaults(false);
    }
  }, []);

  // Compute vault count
  const vaultCount = useMemo(() => vaultedItems.length, [vaultedItems]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<VaultContextValue>(
    () => ({
      vaultedItems,
      isLoadingVaults,
      vaultError,
      vaultCount,
      addVault,
      removeVault,
      refreshVaults
    }),
    [vaultedItems, isLoadingVaults, vaultError, vaultCount, addVault, removeVault, refreshVaults]
  );

  return (
    <VaultContext.Provider value={contextValue}>
      {children}
    </VaultContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

/**
 * useVault hook
 * Accesses the vault context from any component within the provider
 *
 * @throws Error if used outside of VaultProvider
 */
export function useVault(): VaultContextValue {
  const context = useContext(VaultContext);

  if (context === null) {
    throw new Error('useVault must be used within a VaultProvider');
  }

  return context;
}

// ============================================================================
// Exports
// ============================================================================

export { VaultContext };
